/** @typedef {import('../types').Boundaries} Boundaries */
/** @typedef {import('../types').ImagePosition} ImagePosition */
/** @typedef {import('../types').ZoomStartValues} ZoomStartValues */

// Number to buff ratio of pointer distance from center
// Larger number above 1.0 means mouse does not have to go as close to the edge of the window to see the edge of the image
const RATIO_BUFFER = 1.2;
const DEFAULT_HORIZONTAL_POSITION = -50;
/**
 *
 * @param {number} client
 * @param {number} windowDim
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function mouseTargetPosition(client, windowDim, min, max) {
  // client: top, left position => 0, 0
  // Negative ratio is above or to the left of center
  const ratioFromCenter = ((client - windowDim / 2) / (windowDim / 2)) * RATIO_BUFFER;
  const center = min + (max - min) / 2;
  const halfDistance = max - center;
  // Negative result shifts image to the top/left to show the bottm/right of image
  return center + -1 * ratioFromCenter * halfDistance;
}
/**
 *
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function getCenterPosition(min, max) {
  return min + (max - min) / 2;
}
/**
 *
 * @param {import('vue').Ref<boolean>} ticking
 * @param {() => void} callback
 */
export function requestElementUpdate(ticking, callback) {
  const reqAnimationFrame = window.requestAnimationFrame || ((cb) => window.setTimeout(cb, 1000 / 60));
  const animationFrameCallback = () => {
    callback();
    ticking.value = false;
  };
  if (!ticking.value) {
    reqAnimationFrame(animationFrameCallback);
    ticking.value = true;
  }
}

/**
 *
 * @returns {ImagePosition}
 */
export const getDefaultPosition = () => ({
  posX: 0,
  posY: 0,
  atLeft: false,
  atRight: false,
  atTop: false,
  atBottom: false,
});

/**
 *
 * @param {number} scale
 * @param {ZoomStartValues} zoomStartValues
 * @returns {Boundaries}
 *
 */
export function getBoundaryPositions(scale, zoomStartValues, isTopLevelZoomer) {
  let rightOffset = 90;
  let leftOffset = 10;

  if (isTopLevelZoomer) {
    rightOffset = 0;
    leftOffset = 0;
  }

  const { imageWidth, imageHeight, vpLeftToImageCenterX, vpRightToImageCenterX, vpTopToImageCenterY, vpBottomToImageCenterY } = zoomStartValues;

  return {
    xMax: Math.ceil((scale * imageWidth) / 2 - vpLeftToImageCenterX) - leftOffset,
    xMin: Math.ceil(vpRightToImageCenterX - (scale * imageWidth) / 2) - rightOffset,
    yMax: Math.ceil((scale * imageHeight) / 2 - vpTopToImageCenterY),
    yMin: Math.ceil(vpBottomToImageCenterY - (scale * imageHeight) / 2),
  };
}

/**
 *
 * @param {number} val
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export const getBoundedValue = (val, min, max) => Math.max(Math.min(val, max), min);

/**
 *
 * @param {{posX: number, posY: number}} requestedPosition
 * @param {import('vue').UnwrapRef<ImagePosition>} prevPosition
 * @param {Boundaries} boundaries
 * @param {object} options
 * @param {boolean} [options.forceBoundary]
 * @param {boolean} [options.maintainBoundary]
 * @param {boolean} [options.atHorBoundaries]
 * @param {boolean} [options.atVertBoundaries]
 * @returns {{posX: number, posY: number, isMaintainingBoundary: boolean}}
 */
export function getFinalPosition(requestedPosition, prevPosition, boundaries, options) {
  const { posX: reqX, posY: reqY } = requestedPosition;
  const { posX: prevX, posY: prevY, atRight, atLeft, atTop, atBottom } = prevPosition;
  const { xMin, xMax, yMin, yMax } = boundaries;
  const forceBoundary = !!options.forceBoundary;
  const maintainBoundary = !!options.maintainBoundary;
  const atHorBoundaries = !!options.atHorBoundaries;
  const atVertBoundaries = !!options.atVertBoundaries;
  const isInRange = (val, min, max) => min <= val && val <= max;
  let isMaintainingBoundary = false;

  /**
   *
   * @param {number} reqCoord
   * @param {number} prevCoord
   * @param {number} min
   * @param {number} max
   * @param {{atMin: boolean, atMax: boolean}} prevAtBoundary
   * @param {{[atHorBoundaries]: boolean, [atVertBoundaries]: boolean}} atBoundary
   * @returns {number}
   */
  function getFinalCoordinate(reqCoord, prevCoord, min, max, prevAtBoundary, atBoundary) {
    if (atBoundary.atHorBoundaries || atBoundary.atVertBoundaries) return atBoundary.atHorBoundaries ? DEFAULT_HORIZONTAL_POSITION : prevCoord;

    let coord = reqCoord;

    if (maintainBoundary && (prevAtBoundary.atMin || prevAtBoundary.atMax)) {
      // Image was previously positioned at a boundary. Maintain the boundary position
      coord = prevAtBoundary.atMin ? min : max;
      isMaintainingBoundary = true;
    } else if (forceBoundary || isInRange(prevCoord, min, max)) {
      // Image was previously within boundaries. Ensure image continues to stay within the boundaries
      coord = getBoundedValue(reqCoord, min, max);

      // Previous coordinate was not within boundaries. Ensure new position does not move further away from boundaries
      // This scenario can occur when panning a partially pinch to zoomed image
    } else if (reqCoord < min) {
      coord = Math.max(reqCoord, prevCoord);
    } else if (reqCoord > max) {
      coord = Math.min(reqCoord, prevCoord);
    }

    return coord;
  }

  return {
    posX: getFinalCoordinate(
      reqX,
      prevX,
      xMin,
      xMax,
      {
        atMin: atRight,
        atMax: atLeft,
      },
      { atHorBoundaries },
    ),
    posY: getFinalCoordinate(
      reqY,
      prevY,
      yMin,
      yMax,
      {
        atMin: atBottom,
        atMax: atTop,
      },
      { atVertBoundaries },
    ),
    isMaintainingBoundary,
  };
}

/**
 *
 * @param {HTMLElement} imageEl
 * @param {import('vue').UnwrapRef<{posX: number, posY: number, atLeft: boolean, atBottom: boolean, atRight: boolean, atTop: boolean}>} position
 * @param {import('../types').ZoomStates} zoomStates
 */
export function updateImageTransform(imageEl, position, zoomStates) {
  if (!imageEl) return;
  const imageElement = imageEl;
  const transform = !zoomStates.isZoomedIn
    ? 'scale(1)'
    : `translate3d(${position.posX}px, ${position.posY}px, 0)
     scale3d(${zoomStates.scale}, ${zoomStates.scale}, 1)`;

  imageElement.style['z-index'] = !zoomStates.isZoomedIn ? 2 : 10;
  imageElement.style.webkitTransform = transform;
  imageElement.style.mozTransform = transform;
  imageElement.style.transform = transform;
}

/**
 *
 * @param {number} value
 * @param {number} change
 * @returns {number}
 */
export const closerToZero = (value, change) => {
  const sign = value < 0 ? -1 : 1;
  return Math.max(Math.abs(value) + change, 0) * sign;
};

/**
 * Update &wid query parameter on <picture> sources and <img>.
 *
 * Delete it if experiment is unsuccessful
 * In case of scaling consider moving this functionality into Picture component
 *
 * @param {HTMLImageElement} imgEl - The <img> element (typically inside a <picture>).
 * @param {number|string} [newWid=1500] - Default value for the `wid` query parameter.
 * @param {number|string} [newMobileWid=900] - Override for sources with media="(max-width: 599px)".
 * @returns {void}
 */
export function updatePictureWid(imgEl, newWid = 1500, newMobileWid = 900) {
  if (!imgEl) return;

  const picture = imgEl.closest('picture');

  const updateWid = (urlStr, widValue) => {
    try {
      const url = new URL(urlStr);
      url.searchParams.set('wid', widValue);
      return url.toString();
    } catch {
      console.warn('Invalid URL:', urlStr);
      return urlStr;
    }
  };

  // Note: srcset may contain multiple canditates (e.g., "a.jpg 1x, b.jpg 2x")
  // this handles one url case
  if (picture) {
    picture.querySelectorAll('source').forEach((source) => {
      if (!source.srcset) return;

      const widForThisSource = source.media?.trim() === '(max-width: 599px)' ? newMobileWid : newWid;

      source.srcset = updateWid(source.srcset.trim(), widForThisSource);
    });
  }

  if (imgEl.src) {
    imgEl.src = updateWid(imgEl.src, newWid);
  }
}
